import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import Logger from '@/lib/logger';
import { isValidUrl } from '@/lib/utils';
import { getConsent } from '@/lib/consent';
import { clearAmplitudeExperiment } from './amplitudeExperiment';
import type { AnonymousAmplitudeIdentity } from './anonymousIdentity';
import { applyAnonymousAmplitudePrivacy } from './anonymousPrivacy';
import { AD_TRACKING_CONSENT_EVENT_PROPERTY } from './constants';
import { createAmplitudeMemoryStorage } from './memoryStorage';
import { drainPendingAmplitudeItems, enqueuePendingAmplitudeItem } from './pendingEvents';
import {
  enrichSessionCampaignAttributionEvent,
  teardownSessionCampaignAttribution,
  type SessionCampaignTeardownReason,
} from './sessionCampaignAttribution';
import {
  clearAmplitudeCookies,
  clearAmplitudePersistedStorage,
  clearAmplitudeSessionReplayIndexedDb,
} from './storage';

const logger = new Logger('Amplitude');

let initialized = false;
let sessionReplayPluginRef: ReturnType<typeof sessionReplayPlugin> | null = null;
let sessionReplayAdded = false;
let consentEventPropertiesPluginAdded = false;
let sessionCampaignAttributionPluginAdded = false;
let anonymousPrivacyPluginAdded = false;

type AmplitudeBrowserConfig = amplitude.Types.BrowserConfig;
type AmplitudeCookieStorage = AmplitudeBrowserConfig['cookieStorage'];
type AmplitudeUserSession = Parameters<AmplitudeCookieStorage['set']>[1];
type AmplitudeIdentifyValue = Parameters<amplitude.Identify['set']>[1];
type AmplitudeStartupState = 'idle' | 'enabling' | 'enabled' | 'failed' | 'disabled';
export type AmplitudeSessionReplayStorage = 'idb' | 'memory';

const COOKIE_STORAGE_CAPTURE_PLUGIN = 'ft-amplitude-cookie-storage-capture';
const CONSENT_EVENT_PROPERTIES_PLUGIN = 'ft-amplitude-consent-event-properties';
const SESSION_CAMPAIGN_ATTRIBUTION_PLUGIN = 'ft-amplitude-session-campaign-attribution';
const ANONYMOUS_PRIVACY_PLUGIN = 'ft-amplitude-anonymous-privacy';

let startupState: AmplitudeStartupState = 'idle';

const removeNonCanonicalUserIdProperty = (
  eventProperties: Record<string, unknown>
): Record<string, unknown> => {
  const properties = { ...eventProperties };
  // Member identity is canonical only in Amplitude's top-level user_id field.
  delete properties.user_id;
  return properties;
};

// Snapshot identity synchronously so logout events retain it before the immediate SDK reset.
const trackWithCurrentAmplitudeIdentity = (
  eventName: string,
  eventProperties: Record<string, unknown>,
  eventOptions?: amplitude.Types.EventOptions
): void => {
  const userId = amplitude.getUserId();
  const deviceId = amplitude.getDeviceId();

  amplitude.track(eventName, eventProperties, {
    ...eventOptions,
    ...(userId != null && { user_id: userId }),
    ...(deviceId != null && { device_id: deviceId }),
  });
};

// Live BrowserConfig, captured lazily in the teardown path. Teardown swaps identity and event-queue storage to memory and takes the SDK offline so deferred writes or flushes cannot recreate
// `AMP_<prefix>` storage after it is cleared (PD-5890).
// Left pointed at memory on a same-session re-accept — analytics-only gap until the next full page load rebuilds the instance, never a compliance one.
let amplitudeConfigRef: AmplitudeBrowserConfig | null = null;

// Captures the live BrowserConfig via a temporary enrichment plugin (plugins receive it in `setup`), since the SDK exposes no public accessor. Called lazily from the teardown path (not enable), so the redirect still works when a withdraw interrupts enable before it would have captured. `setup()` runs before `add().promise` resolves and the captured reference outlives the plugin, so we remove it immediately afterwards rather than leaving a no-op `execute` registered.
const captureAmplitudeConfig = async (): Promise<void> => {
  if (amplitudeConfigRef) {
    return;
  }

  const plugin: amplitude.Types.EnrichmentPlugin = {
    name: COOKIE_STORAGE_CAPTURE_PLUGIN,
    type: 'enrichment',
    setup: async (config) => {
      amplitudeConfigRef = config as AmplitudeBrowserConfig;
    },
    execute: async (event) => event,
  };

  await amplitude.add(plugin).promise;

  try {
    await amplitude.remove(COOKIE_STORAGE_CAPTURE_PLUGIN).promise;
  } catch (error) {
    logger.warn('[Amplitude] Failed to remove cookie-storage capture plugin', error);
  }
};

const redirectAmplitudeStorageToMemory = (): void => {
  if (!amplitudeConfigRef) {
    return;
  }

  amplitudeConfigRef.cookieStorage = createAmplitudeMemoryStorage<AmplitudeUserSession>();
  amplitudeConfigRef.storageProvider = createAmplitudeMemoryStorage<amplitude.Types.Event[]>();
  amplitudeConfigRef.offline = true;
};

const addConsentEventPropertiesPlugin = async (): Promise<void> => {
  if (consentEventPropertiesPluginAdded) {
    return;
  }

  const plugin: amplitude.Types.EnrichmentPlugin = {
    name: CONSENT_EVENT_PROPERTIES_PLUGIN,
    type: 'enrichment',
    setup: async (config) => {
      amplitudeConfigRef = config as AmplitudeBrowserConfig;
    },
    execute: async (event) => ({
      ...event,
      event_properties: {
        ...event.event_properties,
        [AD_TRACKING_CONSENT_EVENT_PROPERTY]:
          event.event_properties?.[AD_TRACKING_CONSENT_EVENT_PROPERTY] ?? getConsent().allowed,
      },
    }),
  };

  await amplitude.add(plugin).promise;
  consentEventPropertiesPluginAdded = true;
};

const addSessionCampaignAttributionPlugin = async (): Promise<void> => {
  if (sessionCampaignAttributionPluginAdded) {
    return;
  }

  const plugin: amplitude.Types.EnrichmentPlugin = {
    name: SESSION_CAMPAIGN_ATTRIBUTION_PLUGIN,
    type: 'enrichment',
    setup: async () => undefined,
    execute: async (event) => enrichSessionCampaignAttributionEvent(event, window.location.search),
  };

  await amplitude.add(plugin).promise;
  sessionCampaignAttributionPluginAdded = true;
};

const addAnonymousPrivacyPlugin = async (): Promise<void> => {
  if (anonymousPrivacyPluginAdded) {
    await amplitude.remove(ANONYMOUS_PRIVACY_PLUGIN).promise;
  }

  const plugin: amplitude.Types.EnrichmentPlugin = {
    name: ANONYMOUS_PRIVACY_PLUGIN,
    type: 'enrichment',
    setup: async () => undefined,
    execute: async (event) =>
      event.event_properties?.[AD_TRACKING_CONSENT_EVENT_PROPERTY] === false
        ? applyAnonymousAmplitudePrivacy(event)
        : event,
  };

  await amplitude.add(plugin).promise;
  anonymousPrivacyPluginAdded = true;
};

export const getAmplitudeApiKey = () => process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY || '';

export const getAmplitudeServerUrl = (): string | undefined => {
  const serverUrl = process.env.NEXT_PUBLIC_AMPLITUDE_SERVER_URL;

  if (!serverUrl) {
    return undefined;
  }

  if (!isValidUrl(serverUrl)) {
    logger.warn('[Amplitude] Invalid server URL provided:', serverUrl);
    return undefined;
  }

  return serverUrl;
};

export const isAmplitudeConfigured = () =>
  process.env.NEXT_PUBLIC_AMPLITUDE_DISABLED !== 'true' && Boolean(getAmplitudeApiKey());

export const isAmplitudeEnabled = () => startupState === 'enabled';
export const isConsentedAmplitudeEnabled = isAmplitudeEnabled;

export const optOutConsentedAmplitudeImmediately = (): void => {
  if (initialized) {
    amplitude.setOptOut(true);
  }
};

export const prepareAmplitudeStartupRetry = (): void => {
  if (startupState === 'failed') {
    startupState = 'enabling';
  }
};

const flushPendingAmplitudeItems = (): void => {
  // Consent may change while anonymous startup is pending, leaving this as the only flush.
  // Event-time consent ensures the privacy plugin still sanitizes queued anonymous events.
  drainPendingAmplitudeItems().forEach((item) => {
    if (item.type === 'operation') {
      try {
        item.execute();
      } catch (error) {
        logger.error('[Amplitude] Pending operation failed', error);
      }
      return;
    }

    trackWithCurrentAmplitudeIdentity(item.eventName, item.eventProperties, { time: item.time });
  });
};

export const runConsentedAmplitudeOperation = (execute: () => void): void => {
  if (!getConsent().allowed || !isAmplitudeConfigured()) {
    return;
  }

  if (isAmplitudeEnabled()) {
    execute();
    return;
  }

  enqueuePendingAmplitudeItem({
    type: 'operation',
    execute,
    adTrackingConsent: true,
  });
};

export const identifyAmplitudeUser = (
  userId: string,
  traits: Record<string, AmplitudeIdentifyValue | null | undefined>
): void => {
  runConsentedAmplitudeOperation(() => {
    amplitude.setUserId(userId);
    const identify = new amplitude.Identify();
    Object.entries(traits).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        identify.set(key, value);
      }
    });
    amplitude.identify(identify);
  });
};

export const trackAmplitudeEvent = (
  eventName: string,
  eventProperties: Record<string, unknown>
): void => {
  if (!getConsent().allowed || !isAmplitudeConfigured()) {
    return;
  }

  const amplitudeEventProperties = removeNonCanonicalUserIdProperty(eventProperties);

  if (isAmplitudeEnabled()) {
    trackWithCurrentAmplitudeIdentity(eventName, amplitudeEventProperties);
    return;
  }

  enqueuePendingAmplitudeItem({
    type: 'event',
    eventName,
    eventProperties: amplitudeEventProperties,
    time: Date.now(),
    adTrackingConsent: true,
  });
};

export const getAmplitudeExperimentScriptSrc = () => {
  if (!isAmplitudeConfigured()) {
    return null;
  }

  return `//cdn.amplitude.com/script/${getAmplitudeApiKey()}.experiment.js`;
};

const disableSessionReplay = async () => {
  if (!sessionReplayAdded || !sessionReplayPluginRef) {
    return;
  }

  const pluginName = sessionReplayPluginRef.name;

  try {
    // GDPR replay uses memory storage, so plugin teardown stops capture without leaving replay data in IndexedDB. Persisted cleanup below still removes legacy data.
    await amplitude.remove(pluginName).promise;
    sessionReplayPluginRef = null;
    sessionReplayAdded = false;
  } catch (error) {
    logger.error(
      `[Amplitude] Failed to remove session replay plugin (${pluginName}); persisted replay data may remain`,
      error
    );
  }
};

const addSessionReplayPlugin = async (storage: AmplitudeSessionReplayStorage) => {
  if (sessionReplayAdded) {
    return;
  }

  if (storage === 'memory') {
    await clearAmplitudeSessionReplayIndexedDb(getAmplitudeApiKey());
  }

  sessionReplayPluginRef = sessionReplayPlugin({ storeType: storage });
  await amplitude.add(sessionReplayPluginRef).promise;
  sessionReplayAdded = true;
};

const disableAmplitudeSdkInternal = async () => {
  // Capture the live config here (not on enable) so the redirect works even when a withdraw interrupted enable before it would have captured. No-op if already captured; needs the SDK initialized, which only happens client-side. Guarded so a capture failure can't abort teardown.
  if (initialized) {
    try {
      await captureAmplitudeConfig();
    } catch (error) {
      logger.warn('[Amplitude] Failed to capture config during teardown', error);
    }
  }

  // Must run before opt-out/reset/clear so later SDK writes can't re-create the cookie (see amplitudeConfigRef).
  redirectAmplitudeStorageToMemory();

  if (initialized) {
    amplitude.setOptOut(true);
  }

  await disableSessionReplay();

  if (initialized) {
    amplitude.reset();
  }

  clearAmplitudeExperiment();

  const apiKey = getAmplitudeApiKey();
  if (!apiKey) {
    return;
  }

  await clearAmplitudePersistedStorage(apiKey);

  // The redirect stops core identity re-writes, but the attribution plugin caches its own cookieStorage ref at construction, so an `AMP_MKTG_*` write can still slip in — clear again after a tick to catch it.
  clearAmplitudeCookies(apiKey);
  await new Promise((resolve) => {
    setTimeout(resolve, 100);
  });
  clearAmplitudeCookies(apiKey);
};

const disableAmplitudeInternal = async (
  getSessionCampaignTeardownReason: () => SessionCampaignTeardownReason
): Promise<void> => {
  try {
    await disableAmplitudeSdkInternal();
  } finally {
    teardownSessionCampaignAttribution(getSessionCampaignTeardownReason());
  }
};

const runEnable = async (
  anonymousIdentity: AnonymousAmplitudeIdentity | undefined,
  shouldActivate: () => boolean,
  sessionReplayStorage: AmplitudeSessionReplayStorage
): Promise<boolean> => {
  const amplitudeServerUrl = getAmplitudeServerUrl();
  const apiKey = getAmplitudeApiKey();

  await addConsentEventPropertiesPlugin();
  await addSessionCampaignAttributionPlugin();

  if (!initialized) {
    await amplitude.init(apiKey, {
      autocapture: {
        attribution: true,
        pageViews: false,
        sessions: true,
        formInteractions: false,
        fileDownloads: false,
        elementInteractions: false,
      },
      minIdLength: 1,
      optOut: true,
      ...(anonymousIdentity && {
        deviceId: anonymousIdentity.deviceId,
        sessionId: anonymousIdentity.sessionId,
      }),
      ...(amplitudeServerUrl && {
        serverUrl: amplitudeServerUrl,
      }),
    }).promise;
    initialized = true;
  } else if (anonymousIdentity) {
    amplitude.setDeviceId(anonymousIdentity.deviceId);
    amplitude.setSessionId(anonymousIdentity.sessionId);
  }

  if (!shouldActivate()) {
    return false;
  }

  if (amplitudeConfigRef) {
    amplitudeConfigRef.offline = false;
  }

  amplitude.setOptOut(false);

  if (!shouldActivate()) {
    amplitude.setOptOut(true);
    return false;
  }

  await addSessionReplayPlugin(sessionReplayStorage);
  await addAnonymousPrivacyPlugin();

  if (!shouldActivate()) {
    amplitude.setOptOut(true);
    await disableSessionReplay();
    return false;
  }

  startupState = 'enabled';
  flushPendingAmplitudeItems();
  return true;
};

export const enableConsentedAmplitude = async (
  anonymousIdentity: AnonymousAmplitudeIdentity | undefined,
  shouldActivate: () => boolean,
  sessionReplayStorage: AmplitudeSessionReplayStorage
): Promise<boolean> => {
  if (typeof window === 'undefined' || !isAmplitudeConfigured()) {
    startupState = 'disabled';
    return false;
  }

  startupState = 'enabling';

  try {
    const didEnable = await runEnable(anonymousIdentity, shouldActivate, sessionReplayStorage);
    if (!didEnable) {
      startupState = 'disabled';
    }
    return didEnable;
  } catch (error) {
    startupState = 'failed';
    throw error;
  }
};

export const disableConsentedAmplitude = async (
  shouldPreserveStartupRetry: () => boolean = () => false,
  getSessionCampaignTeardownReason: () => SessionCampaignTeardownReason = () => 'withdrawal'
): Promise<void> => {
  try {
    await disableAmplitudeInternal(getSessionCampaignTeardownReason);
  } finally {
    if (!shouldPreserveStartupRetry()) {
      startupState = 'disabled';
    }
  }
};

export const resetAmplitudeUser = () => {
  runConsentedAmplitudeOperation(() => {
    amplitude.reset();
  });
};
